import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = [
  { key: 'offre', label: "Offre d'achat" },
  { key: 'plans', label: 'Plans' },
  { key: 'devis', label: 'Devis travaux' },
  { key: 'compromis', label: 'Compromis signé' },
  { key: 'dossier_bancaire', label: 'Dossier bancaire déposé' },
  { key: 'offre_financement', label: 'Offre de financement reçue' },
  { key: 'devis_signe', label: 'Devis signé' },
  { key: 'acte_authentique', label: 'Acte authentique signé' },
  { key: 'assurance_pno', label: 'Assurance PNO souscrite' },
  { key: 'compteurs', label: 'Compteurs ENEDIS / Eau ouverts' },
  { key: 'reception_travaux', label: 'Réception des travaux' },
  { key: 'levee_reserves', label: 'Levée des réserves' },
  { key: 'mise_en_location', label: 'Mise en location' },
];

export default function ProjectChecklist({ projectId, initial }) {
  const [items, setItems] = useState(null);
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const existing = Array.isArray(initial) && initial.length > 0 ? initial : [];
    const byKey = Object.fromEntries(existing.map(i => [i.key, i]));
    const merged = DEFAULTS.map(d => byKey[d.key] || { ...d, done: false });
    existing.filter(i => i.custom).forEach(i => { if (!merged.some(m => m.key === i.key)) merged.push(i); });
    setItems(merged);
  }, [initial]);

  const persist = useCallback(async (next) => {
    setItems(next);
    setSaving(true);
    try {
      const r = await axios.put(`${API}/projects/${projectId}/checklist`, { items: next });
      setItems(r.data.checklist);
    } catch { /* garde l'etat local */ }
    setSaving(false);
  }, [projectId]);

  if (!items) return null;
  const doneCount = items.filter(i => i.done).length;

  return (
    <div className="realease-card p-5" data-testid="project-checklist">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">Pense-bête du projet</h3>
        <span className="text-xs font-bold text-[#13736D]">{doneCount}/{items.length}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-3">
        <div className="h-full bg-[#13736D] rounded-full transition-all" style={{ width: `${(doneCount / items.length) * 100}%` }} />
      </div>
      <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
        {items.map((it) => (
          <label key={it.key} className={`flex items-start gap-2 text-sm cursor-pointer rounded px-1.5 py-1 hover:bg-gray-50 ${it.done ? 'text-gray-400' : 'text-gray-700'}`}
                 data-testid={`checklist-item-${it.key}`}>
            <input type="checkbox" checked={!!it.done} className="mt-0.5 accent-[#13736D]"
                   onChange={() => persist(items.map(x => x.key === it.key ? { ...x, done: !x.done } : x))} />
            <span className="flex-1">
              <span className={it.done ? 'line-through' : ''}>{it.label}</span>
              {it.done && it.done_by && (
                <span className="block text-[10px] text-gray-400 no-underline">
                  ✓ {it.done_by} — {it.done_at ? new Date(it.done_at).toLocaleDateString('fr-FR') : ''}
                </span>
              )}
            </span>
          </label>
        ))}
      </div>
      <form className="mt-3 flex gap-2" onSubmit={(e) => {
        e.preventDefault();
        const label = newLabel.trim();
        if (!label) return;
        persist([...items, { key: `custom_${Date.now()}`, label, done: false, custom: true }]);
        setNewLabel('');
      }}>
        <input value={newLabel} onChange={(e) => setNewLabel(e.target.value)} placeholder="Ajouter une tâche..."
               className="realease-input text-xs flex-1" data-testid="checklist-add-input" />
        <button type="submit" disabled={saving || !newLabel.trim()}
                className="text-xs px-3 py-1 rounded-lg bg-gray-800 text-white font-semibold disabled:opacity-40"
                data-testid="checklist-add-btn">+</button>
      </form>
    </div>
  );
}
